import { useCallback } from 'react';

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { CostReportAllocationGroupSummaryItem } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ALLOCATION_GROUPS_SUMMARIES_QUERY_KEY = 'allocation-groups-summaries';

type UseAllocationGroupsSummariesQuery = {
  allocationGroupsSummaries?: CostReportAllocationGroupSummaryItem[];
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
  invalidate(): void;
};

type UseAllocationGroupsSummariesQueryProps = {
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  clusterIds?: string[];
  groupId?: string;
  includeIdleCosts?: boolean;
  enabled?: boolean;
};

const getAllocationGroupsSummariesQueryKey = ({
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
  groupId,
  includeIdleCosts,
}: Partial<
  Pick<
    UseAllocationGroupsSummariesQueryProps,
    | 'startTime'
    | 'endTime'
    | 'useListingPrices'
    | 'clusterIds'
    | 'groupId'
    | 'includeIdleCosts'
  >
> = {}) =>
  [
    ALLOCATION_GROUPS_SUMMARIES_QUERY_KEY,
    ...(startTime !== undefined
      ? [
          startTime,
          endTime,
          useListingPrices,
          clusterIds,
          groupId,
          includeIdleCosts,
        ]
      : []),
  ] as const;

const getAllocationGroupsSummariesQueryOptions = ({
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
  groupId,
  includeIdleCosts = false,
  enabled = true,
}: UseAllocationGroupsSummariesQueryProps) =>
  queryOptions({
    queryKey: getAllocationGroupsSummariesQueryKey({
      startTime,
      endTime,
      useListingPrices,
      clusterIds,
      groupId,
      includeIdleCosts,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getAllocationGroupSummaries({
        clusterIds,
        startTime,
        endTime,
        groupId,
        useListingPrices,
        includeIdleResourceCosts: includeIdleCosts,
      });
      return data.items;
    },
    enabled: Boolean(enabled && startTime && endTime),
    refetchOnWindowFocus: false,
  });

export const useAllocationGroupsSummariesQuery = (
  props: UseAllocationGroupsSummariesQueryProps
): UseAllocationGroupsSummariesQuery => {
  const queryClient = useQueryClient();

  const {
    isLoading,
    data: allocationGroupsSummaries,
    error,
    refetch,
  } = useQuery(getAllocationGroupsSummariesQueryOptions(props));

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getAllocationGroupsSummariesQueryKey(),
      }),
    [queryClient]
  );

  return {
    allocationGroupsSummaries,
    isLoading,
    error,
    refetch,
    invalidate,
  };
};
